import { useState, useEffect, createContext, useContext } from 'react'
import { ThemeName, getTheme, ThemeColors, getAllThemeNames } from '../config/themes'

interface ThemeContextType {
  currentTheme: ThemeName
  setTheme: (theme: ThemeName) => void
  colors: ThemeColors
  allThemes: ThemeName[]
}

const THEME_STORAGE_KEY = 'camping_theme'
const DEFAULT_THEME: ThemeName = 'tema5' // Niklitschek Signature - Paleta mejorada

// Crear el contexto
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Hook para usar el tema
export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme debe ser usado dentro de ThemeProvider')
  }
  return context
}

// Custom hook para gestionar el tema
export const useThemeState = () => {
  const [currentTheme, setCurrentThemeState] = useState<ThemeName>(() => {
    // Cargar del localStorage
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return (stored as ThemeName) || DEFAULT_THEME
  })

  const colors = getTheme(currentTheme)
  const allThemes = getAllThemeNames()

  const setTheme = (theme: ThemeName) => {
    setCurrentThemeState(theme)
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }

  // Aplicar tema a nivel de HTML para que Tailwind lo use
  useEffect(() => {
    const root = document.documentElement
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value)
    })
    // Agregar clase al root para cambios CSS dinámicos
    root.classList.remove(...allThemes.map(t => `theme-${t}`))
    root.classList.add(`theme-${currentTheme}`)
  }, [currentTheme, colors, allThemes])

  return {
    currentTheme,
    setTheme,
    colors,
    allThemes,
  }
}
